import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Match } from 'effect';
import { useRecoilValue } from 'recoil';
import { analysisViewRefIdState, proxySetIdState } from 'venn-state';
import type { ProxyTypeEnum, ProxyMeta, FrequencyEnum, FundReturnsRange } from 'venn-api';
import { Loading, ShimmerBlock } from 'venn-ui-kit';
import ProxyTypeOptions from './ProxyTypeOptions';
import { ChangeProxyTypeWarningDialog } from './components/ChangeProxyTypeWarningDialog';
import { RemoveProxyConfirmationDialog } from './components/RemoveProxyConfirmationDialog';
import {
  useHasFF,
  withSuspense,
  type SecondLayerProxyTypeValue,
  type FirstLayerProxyTypeValue,
  createFirstLayerProxyTypeValue,
  createSecondLayerProxyTypeValue,
  getExtrapolateFromValue,
} from 'venn-utils';
import { typeSupportsCategoryPicker, calculateBackfilledRange, type SelectedProxy } from './utils';
import { useBulkProxyErrorMessage } from './useProxyErrorMessage';
import { useInvestmentsReturnsRanges, useProxyReturnsRange } from './useInvestmentsReturnsRanges';
import { isNil } from 'lodash';
import { ExtrapolationToggle } from './components/ExtrapolationToggle';
import { ProxySummaryTable } from './ProxySummaryTable';
import SidePanelOverlay from '../../../side-panel-overlay/SidePanelOverlay';
import type { FundToBulkProxy, FundToProxy } from '../types';
import { BulkCategoryOptions } from './BulkCategoryOptions';
import { SingleInvestmentInfo } from './components/SingleInvestmentInfo';
import { BulkProxySearch } from './components/BulkProxySearch';
import { BulkProxyLegend } from './BulkProxyLegend';
import { DesmoothingLag } from './DesmoothingLag';
import { useProxyDataQuery, useRangeAnalysisQueryBatched, getFirstProxy } from '../../../hooks';
import { ProxyPickerHeader } from './components/ProxyPickerHeaderPrimitives';
import { ProxyConfigurationTabs } from './components/ProxyConfigurationTabs';
import { LayeredProxyTypeSelector } from './components/LayeredProxyTypeSelector';
import { LayeredProxyConfiguration, createLayerableProxyInfo, createNonLayerableProxyInfo } from './proxy-types';
import {
  useProxyPickerState,
  useProxyPickerActions,
  ProxyPickerStateResult,
  ProxyPickerAction,
  ProxyPickerActionsDependencies,
} from './hooks';

type ActiveProxy = { proxyType: string; extrapolate?: boolean } | null;

/**
 * Computes whether extrapolation should be applied based on the active proxy configuration.
 * Returns true if:
 * - The proxy type is 'EXTRAPOLATE' (extrapolation is implicit)
 * - The proxy has an explicit extrapolate: true flag
 */
export const computeShouldExtrapolate = (activeProxy: ActiveProxy): boolean => {
  if (!activeProxy) return false;
  return activeProxy.proxyType === 'EXTRAPOLATE' || ('extrapolate' in activeProxy && activeProxy.extrapolate === true);
};

/**
 * Computes the frequency for the second layer proxy lag input.
 * Uses rawInvestmentReturnsRanges which already handles the fallback chain:
 * server proxy data -> range analysis data -> investment.returnFrequency.
 * This ensures frequency is available even for newly added investments without saved proxy data.
 */
export const computeSecondLayerFrequency = ({
  hasSingleProxy,
  investment,
  rawInvestmentReturnsRanges,
}: {
  hasSingleProxy: boolean;
  investment: (FundToProxy | FundToBulkProxy) | undefined;
  rawInvestmentReturnsRanges: FundReturnsRange[] | undefined;
}): FrequencyEnum | undefined => {
  return hasSingleProxy && investment
    ? (rawInvestmentReturnsRanges?.[0]?.frequency ?? investment.returnFrequency)
    : undefined;
};

/**
 * Determines whether to show the second layer proxy controls (+ button and dropdown).
 * Only shows when:
 * 1. The layered proxy feature flag is enabled
 * 2. No daily frequency investments (daily investments don't support layered proxies)
 * 3. Either:
 *    - Configuration is SingleLayerable AND an investment has been selected as proxy
 *    - Configuration is already Multi (second layer already exists)
 */
export const shouldShowSecondLayerControls = ({
  hasLayeredProxyFF,
  frequencies,
  proxyConfiguration,
}: {
  hasLayeredProxyFF: boolean;
  frequencies: (FrequencyEnum | undefined)[];
  proxyConfiguration: LayeredProxyConfiguration;
}): boolean => {
  return (
    hasLayeredProxyFF &&
    frequencies.some((frequency) => frequency !== 'DAILY') &&
    Match.value(proxyConfiguration).pipe(
      Match.when(LayeredProxyConfiguration.$is('SingleLayerable'), ({ proxy }) => !isNil(proxy.selectedProxy)),
      Match.when(LayeredProxyConfiguration.$is('Multi'), () => true),
      Match.orElse(() => false),
    )
  );
};

export type ProxyMetaWithName = ProxyMeta & { proxyName: string };

interface BulkProxyPickerProps {
  investments: (FundToProxy | FundToBulkProxy)[];
  onProxyChange?: (appliedProxies: ProxyMetaWithName[], failedToProxyFunds: (FundToProxy | FundToBulkProxy)[]) => void;
}

const MANAGE_DATA_SUBTITLE = 'Proxies will be applied across your entire workspace.';
const HEADER_TITLE = 'Proxy investment returns';

const deriveLegacyProxyConfiguration = (
  selectedProxyType: ProxyTypeEnum | undefined,
  selectedProxy: SelectedProxy | null,
  numLags: number,
  shouldExtrapolate: boolean,
): LayeredProxyConfiguration => {
  if (!selectedProxyType || !selectedProxy) {
    return LayeredProxyConfiguration.Empty();
  }

  if (selectedProxyType === 'BACKFILL' && !shouldExtrapolate) {
    return LayeredProxyConfiguration.SingleLayerable({
      proxy: createLayerableProxyInfo({
        selectedProxy,
        numLags,
      }),
    });
  }

  return LayeredProxyConfiguration.SingleNonLayerable({
    proxy: createNonLayerableProxyInfo(selectedProxyType, shouldExtrapolate, {
      selectedProxy,
      numLags,
    }),
  });
};

const BulkProxyPickerInternal = ({ investments, onProxyChange }: BulkProxyPickerProps) => {
  const hasProxyVisibilityFF = useHasFF('proxy_visibility_ff');
  const isStudioOrReport = !isNil(useRecoilValue(analysisViewRefIdState));
  const proxySetId = useRecoilValue(proxySetIdState);
  const investmentIds = investments.map((investment) => investment.id);
  const proxyDataQueryResult = useProxyDataQuery(investmentIds, proxySetId);
  const returnsProxyDataByFund = Object.fromEntries(
    Object.entries(proxyDataQueryResult).map(([fundId, proxyData]) => [fundId, proxyData?.returns ?? ([] as const)]),
  );
  const rangeDataByFund = useRangeAnalysisQueryBatched(investmentIds.map((id) => ({ id, proxySetId })));
  const hasSingleInvestment = investments.length === 1;

  const hasExtrapolationFf = useHasFF('extrapolation_ff');
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');

  const { onClose } = useContext(SidePanelOverlay.Context);

  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [pendingProxyTypeValue, setPendingProxyTypeValue] = useState<FirstLayerProxyTypeValue | null>(null);
  const [removeProxyDialogOpen, setRemoveProxyDialogOpen] = useState(false);
  const [removeSecondLayerDialogOpen, setRemoveSecondLayerDialogOpen] = useState(false);

  const stateResult = useProxyPickerState({
    investments,
    proxySetId,
    returnsProxyDataByFund,
  });

  const { selectedProxyType, selectedProxy, numLags, shouldExtrapolate, extrapolationToggleState } = Match.value(
    stateResult,
  ).pipe(
    Match.tag('Legacy', ({ state }) => ({
      selectedProxyType: state.selectedProxyType,
      selectedProxy: state.selectedProxy,
      numLags: state.numLags,
      shouldExtrapolate: state.shouldExtrapolate,
      extrapolationToggleState: state.extrapolationToggleState,
    })),
    Match.tag('Layered', ({ state }) => {
      const activeProxy = Match.value(state.configuration).pipe(
        Match.tag('Empty', () => null),
        Match.tag('SingleLayerable', ({ proxy }) => proxy),
        Match.tag('SingleNonLayerable', ({ proxy }) => proxy),
        Match.tag('Multi', ({ firstLayer, secondLayer }) => (state.activeTab === 'second' ? secondLayer : firstLayer)),
        Match.exhaustive,
      );
      return {
        selectedProxyType: activeProxy?.proxyType,
        selectedProxy: activeProxy?.selectedProxy ?? null,
        numLags: activeProxy?.numLags ?? 0,
        shouldExtrapolate: computeShouldExtrapolate(activeProxy),
        extrapolationToggleState: undefined,
      };
    }),
    Match.exhaustive,
  );
  const supportsCategoryPicker = typeSupportsCategoryPicker(selectedProxyType!);

  const isLayered = ProxyPickerStateResult.$is('Layered');
  const proxyConfiguration = isLayered(stateResult)
    ? stateResult.state.configuration
    : deriveLegacyProxyConfiguration(selectedProxyType, selectedProxy, numLags, shouldExtrapolate);
  const isMultiConfig = LayeredProxyConfiguration.$is('Multi')(proxyConfiguration);

  const firstLayerProxyTypeValue = useMemo((): FirstLayerProxyTypeValue | undefined => {
    return Match.value(proxyConfiguration).pipe(
      Match.tag('Empty', () => undefined),
      Match.tag('SingleLayerable', ({ proxy }) => createFirstLayerProxyTypeValue(proxy.proxyType, proxy.extrapolate)),
      Match.tag('SingleNonLayerable', ({ proxy }) =>
        createFirstLayerProxyTypeValue(proxy.proxyType, 'extrapolate' in proxy ? proxy.extrapolate : false),
      ),
      Match.tag('Multi', ({ firstLayer }) =>
        createFirstLayerProxyTypeValue(firstLayer.proxyType, firstLayer.extrapolate),
      ),
      Match.exhaustive,
    );
  }, [proxyConfiguration]);

  const { firstLayerProxy, secondLayerProxy } = useMemo(() => {
    return Match.value(proxyConfiguration).pipe(
      Match.tag('Empty', () => ({
        firstLayerProxy: null,
        secondLayerProxy: null,
      })),
      Match.tag('SingleLayerable', ({ proxy }) => ({
        firstLayerProxy: proxy.selectedProxy,
        secondLayerProxy: null,
      })),
      Match.tag('SingleNonLayerable', ({ proxy }) => ({
        firstLayerProxy: proxy.selectedProxy,
        secondLayerProxy: null,
      })),
      Match.tag('Multi', ({ firstLayer, secondLayer }) => ({
        firstLayerProxy: firstLayer.selectedProxy,
        secondLayerProxy: secondLayer.selectedProxy,
      })),
      Match.exhaustive,
    );
  }, [proxyConfiguration]);

  const rawInvestmentReturnsRanges = useInvestmentsReturnsRanges(investments, returnsProxyDataByFund, rangeDataByFund);
  const { data: firstLayerProxyReturnsRange } = useProxyReturnsRange(firstLayerProxy?.id);
  const { data: secondLayerProxyReturnsRange } = useProxyReturnsRange(secondLayerProxy?.id);

  const effectiveSecondLayerRanges = useMemo(() => {
    const isBackfill =
      LayeredProxyConfiguration.$is('SingleLayerable')(proxyConfiguration) ||
      LayeredProxyConfiguration.$is('Multi')(proxyConfiguration);
    if (!isBackfill || !firstLayerProxyReturnsRange) {
      return rawInvestmentReturnsRanges;
    }
    return rawInvestmentReturnsRanges?.map((range) => calculateBackfilledRange(range, firstLayerProxyReturnsRange));
  }, [proxyConfiguration, rawInvestmentReturnsRanges, firstLayerProxyReturnsRange]);

  const validationState = useBulkProxyErrorMessage({
    investments,
    proxyConfiguration,
    rawInvestmentReturnsRanges,
    effectiveSecondLayerRanges,
    firstLayerProxyReturnsRange,
    secondLayerProxyReturnsRange,
  });

  const suggestedNumLags = useMemo(() => {
    return hasSingleInvestment && validationState.state === 'ready'
      ? investments[0] && validationState.investmentInfo[investments[0].id]?.suggestedLags
      : 0;
  }, [validationState, hasSingleInvestment, investments]);

  const actionDependencies = useMemo(() => {
    const baseState = {
      investments,
      proxySetId,
      validationState,
      hasSingleProxy: hasSingleInvestment,
      isResetOperation: hasProxyVisibilityFF && isStudioOrReport,
    };
    const baseMutations = { onProxyChange, onClose, setIsUpdating: () => {} };

    return Match.value(stateResult).pipe(
      Match.tag('Legacy', ({ state, mutations }) =>
        ProxyPickerActionsDependencies.Legacy({
          state: { proxy: state, ...baseState },
          mutations: { proxy: mutations, ...baseMutations },
        }),
      ),
      Match.tag('Layered', ({ state, mutations }) =>
        ProxyPickerActionsDependencies.Layered({
          state: {
            proxy: { configuration: state.configuration, activeTab: state.activeTab },
            ...baseState,
          },
          mutations: { proxy: mutations, ...baseMutations },
        }),
      ),
      Match.exhaustive,
    );
  }, [
    stateResult,
    investments,
    proxySetId,
    validationState,
    hasSingleInvestment,
    onProxyChange,
    onClose,
    hasProxyVisibilityFF,
    isStudioOrReport,
  ]);

  const { performAction, isUpdating } = useProxyPickerActions(actionDependencies);

  const onSelectCategory = useCallback(
    (proxy: { id: string; name: string } | null, lags?: number | undefined) => {
      performAction(ProxyPickerAction.SelectProxy({ proxy }));
      if (lags !== undefined) {
        performAction(ProxyPickerAction.SetNumLags({ lags }));
      }
    },
    [performAction],
  );

  const onFirstLayerProxyTypeSelect = useCallback(
    (value: FirstLayerProxyTypeValue) => {
      const isNewValueNonLayerable = value.proxyType !== 'BACKFILL' || value.extrapolate;

      if (isMultiConfig && isNewValueNonLayerable) {
        setPendingProxyTypeValue(value);
        setWarningDialogOpen(true);
      } else {
        performAction(ProxyPickerAction.SelectProxyType({ value }));
      }
    },
    [performAction, isMultiConfig],
  );

  const onConfirmProxyTypeChange = useCallback(() => {
    if (pendingProxyTypeValue) {
      performAction(ProxyPickerAction.RemoveSecondLayer());
      performAction(ProxyPickerAction.SelectProxyType({ value: pendingProxyTypeValue }));
      setPendingProxyTypeValue(null);
    }
  }, [pendingProxyTypeValue, performAction]);

  const onLegacyProxyTypeChange = useCallback(
    (proxyType: ProxyTypeEnum) => {
      performAction(ProxyPickerAction.SelectProxyType({ value: createFirstLayerProxyTypeValue(proxyType, false) }));
    },
    [performAction],
  );

  const onToggleExtrapolation = useCallback(() => {
    performAction(ProxyPickerAction.SetExtrapolate({ extrapolate: !shouldExtrapolate }));
  }, [performAction, shouldExtrapolate]);

  const onLagUpdate = useCallback(
    (lags: number) => {
      performAction(ProxyPickerAction.SetNumLags({ lags }));
    },
    [performAction],
  );

  const setSelectedProxy = useCallback(
    (proxy: { id: string; name: string } | null) => {
      performAction(ProxyPickerAction.SelectProxy({ proxy }));
    },
    [performAction],
  );

  const onSecondLayerProxyTypeSelect = useCallback(
    (value: SecondLayerProxyTypeValue) => {
      performAction(ProxyPickerAction.SelectSecondLayerProxyType({ value }));
    },
    [performAction],
  );

  const onRemoveSecondLayer = useCallback(() => {
    performAction(ProxyPickerAction.RemoveSecondLayer());
  }, [performAction]);

  const onRemoveSecondLayerWithConfirmation = useCallback(() => {
    const hasSecondLayerProxy = isMultiConfig && proxyConfiguration.secondLayer.selectedProxy !== null;

    if (hasSecondLayerProxy) {
      setRemoveSecondLayerDialogOpen(true);
    } else {
      performAction(ProxyPickerAction.RemoveSecondLayer());
    }
  }, [isMultiConfig, proxyConfiguration, performAction]);

  const onSecondLayerLagUpdate = useCallback(
    (lags: number) => {
      if (isLayered(stateResult)) {
        stateResult.mutations.setSecondLayerNumLags(lags);
      }
    },
    [isLayered, stateResult],
  );

  const proceedMessage = useMemo(() => {
    const validInvestments =
      validationState.state === 'ready'
        ? investments.filter((investment) => !validationState.investmentInfo[investment.id]?.disableSave).length
        : investments.length;

    const isMultiProxy =
      hasLayeredProxyFF &&
      isLayered(stateResult) &&
      LayeredProxyConfiguration.$is('Multi')(stateResult.state.configuration);

    const proxyWord = isMultiProxy ? 'proxies' : 'proxy';

    if (!hasSingleInvestment) {
      return `Apply returns ${proxyWord} to ${validInvestments} of ${investments.length} investments`;
    }
    return `Apply returns ${proxyWord}`;
  }, [validationState, investments, hasLayeredProxyFF, hasSingleInvestment, isLayered, stateResult]);

  const removeProxyProps = useMemo(() => {
    const investment = investments[0];
    if (!investment) {
      return {};
    }
    const proxyData = returnsProxyDataByFund[investment.id] ?? ([] as const);
    if (!hasSingleInvestment || proxyData.length === 0) {
      return {};
    }

    const getSecondaryLabel = () => {
      if (hasProxyVisibilityFF && isStudioOrReport) {
        return 'Reset Proxy';
      }
      return isMultiConfig ? 'Remove Proxies' : 'Remove Proxy';
    };

    return {
      secondaryDisabled: isUpdating,
      secondaryLabel: getSecondaryLabel(),
      onSecondaryClick: () => setRemoveProxyDialogOpen(true),
      secondaryDestructive: true,
    };
  }, [
    investments,
    returnsProxyDataByFund,
    hasSingleInvestment,
    isUpdating,
    hasProxyVisibilityFF,
    isStudioOrReport,
    isMultiConfig,
  ]);

  const frequencies = investments.map(
    (investment, i) => rawInvestmentReturnsRanges?.[i]?.frequency ?? investment.returnFrequency,
  );

  const showSecondLayerControls = shouldShowSecondLayerControls({
    hasLayeredProxyFF,
    frequencies,
    proxyConfiguration,
  });

  const {
    secondLayerNumLags,
    secondLayerFrequency,
    secondLayerProxyTypeValue,
    currentSecondLayerType,
    currentSecondLayerExtrapolate,
  } = useMemo(() => {
    return {
      secondLayerNumLags: isMultiConfig ? proxyConfiguration.secondLayer.numLags : 0,
      secondLayerFrequency: computeSecondLayerFrequency({
        hasSingleProxy: hasSingleInvestment,
        investment: investments[0],
        rawInvestmentReturnsRanges,
      }),
      secondLayerProxyTypeValue: isMultiConfig
        ? createSecondLayerProxyTypeValue(
            proxyConfiguration.secondLayer.proxyType,
            getExtrapolateFromValue(proxyConfiguration.secondLayer),
          )
        : undefined,
      currentSecondLayerType: isMultiConfig ? proxyConfiguration.secondLayer.proxyType : undefined,
      currentSecondLayerExtrapolate: isMultiConfig
        ? getExtrapolateFromValue(proxyConfiguration.secondLayer)
        : undefined,
    };
  }, [proxyConfiguration, hasSingleInvestment, investments, rawInvestmentReturnsRanges, isMultiConfig]);

  const showRequireUpdatingOtherProxy = !!(firstLayerProxyTypeValue && secondLayerProxyTypeValue);

  return (
    <>
      {dialogContainerRef.current && pendingProxyTypeValue && currentSecondLayerType && (
        <ChangeProxyTypeWarningDialog
          open={warningDialogOpen}
          onOpenChange={setWarningDialogOpen}
          onConfirm={onConfirmProxyTypeChange}
          newProxyTypeValue={pendingProxyTypeValue}
          currentSecondLayerType={currentSecondLayerType}
          currentSecondLayerExtrapolate={currentSecondLayerExtrapolate}
          container={dialogContainerRef.current}
        />
      )}
      {dialogContainerRef.current && (
        <RemoveProxyConfirmationDialog
          open={removeProxyDialogOpen}
          onOpenChange={setRemoveProxyDialogOpen}
          onConfirm={() => performAction(ProxyPickerAction.Remove())}
          isMultiple={isMultiConfig}
          isResetOperation={hasProxyVisibilityFF && isStudioOrReport}
          container={dialogContainerRef.current}
        />
      )}
      {dialogContainerRef.current && (
        <RemoveProxyConfirmationDialog
          open={removeSecondLayerDialogOpen}
          onOpenChange={setRemoveSecondLayerDialogOpen}
          onConfirm={onRemoveSecondLayer}
          isMultiple={false}
          isResetOperation={false}
          container={dialogContainerRef.current}
        />
      )}
      <div ref={dialogContainerRef} className="flex flex-col h-full relative">
        {isUpdating && (
          <div className="absolute z-intercom-front-2 top-0 left-0 size-full flex place-items-center bg-white bg-opacity-30 opacity-0 animate-[fade-in-opacity_1s_ease-in-out_1s_1_normal_forwards]">
            <Loading pageLoader />
          </div>
        )}
        <ProxyPickerHeader
          title={HEADER_TITLE}
          subtitle={
            hasProxyVisibilityFF && isStudioOrReport
              ? `Applies to all instances of ${investments[0]?.name} in this view.`
              : MANAGE_DATA_SUBTITLE
          }
        />
        <SidePanelOverlay.Body className="!p-0 flex flex-col flex-grow overflow-y-auto mb-[64px]">
          <div className="flex flex-col flex-grow gap-[16px]">
            <div className="px-[24px] pt-[16px] flex flex-col gap-[16px]">
              {hasSingleInvestment && investments[0] && (
                <SingleInvestmentInfo
                  investment={investments[0]}
                  proxyData={getFirstProxy(returnsProxyDataByFund[investments[0].id] ?? ([] as const))}
                  proxyConfiguration={proxyConfiguration}
                  rangeData={rangeDataByFund[investments[0].id]}
                />
              )}

              {hasLayeredProxyFF ? (
                <LayeredProxyTypeSelector
                  firstLayerProxyTypeValue={firstLayerProxyTypeValue}
                  onFirstLayerProxyTypeSelect={onFirstLayerProxyTypeSelect}
                  investments={investments}
                  rawInvestmentRanges={rawInvestmentReturnsRanges}
                  firstLayerProxyRange={firstLayerProxyReturnsRange ?? undefined}
                  secondLayerProxyRange={secondLayerProxyReturnsRange ?? undefined}
                  withExtrapolation={hasExtrapolationFf}
                  showSecondLayerControls={showSecondLayerControls}
                  secondLayerProxyTypeValue={secondLayerProxyTypeValue}
                  onSecondLayerProxyTypeSelect={onSecondLayerProxyTypeSelect}
                  proxyConfiguration={proxyConfiguration}
                  onRemoveSecondLayer={onRemoveSecondLayerWithConfirmation}
                  secondLayerNumLags={secondLayerNumLags}
                  secondLayerSuggestedNumLags={suggestedNumLags}
                  onSecondLayerLagUpdate={onSecondLayerLagUpdate}
                  secondLayerFrequency={secondLayerFrequency}
                  selectedProxyType={selectedProxyType}
                  numLags={numLags}
                  suggestedNumLags={suggestedNumLags}
                  onLagUpdate={onLagUpdate}
                  proxyData={
                    investments[0]
                      ? getFirstProxy(returnsProxyDataByFund[investments[0].id] ?? ([] as const))
                      : undefined
                  }
                />
              ) : (
                <div className="flex flex-row items-end gap-[8px]">
                  <ProxyTypeOptions
                    investments={investments}
                    rawInvestmentRanges={rawInvestmentReturnsRanges}
                    selectedProxyRange={firstLayerProxyReturnsRange}
                    selectedProxyType={selectedProxyType}
                    withInvalidTypes
                    onSelectProxyType={onLegacyProxyTypeChange}
                  />
                  {hasExtrapolationFf && extrapolationToggleState && (
                    <ExtrapolationToggle
                      extrapolationToggleState={extrapolationToggleState}
                      onToggle={onToggleExtrapolation}
                    />
                  )}
                  <DesmoothingLag
                    investments={investments}
                    selectedProxyType={selectedProxyType}
                    numLags={numLags || suggestedNumLags}
                    suggestedNumLags={suggestedNumLags}
                    onLagUpdate={onLagUpdate}
                    proxyData={
                      investments[0]
                        ? getFirstProxy(returnsProxyDataByFund[investments[0].id] ?? ([] as const))
                        : undefined
                    }
                  />
                </div>
              )}

              {!hasLayeredProxyFF && (
                <>
                  <div className="flex items-center justify-between">
                    {hasSingleInvestment && supportsCategoryPicker ? (
                      <>
                        <SearchLabel>Select a proxy</SearchLabel>
                        <BulkProxyLegend />
                      </>
                    ) : (
                      <SearchLabel>Select an investment to use as a proxy</SearchLabel>
                    )}
                  </div>
                  {supportsCategoryPicker ? (
                    <BulkCategoryOptions
                      investments={investments}
                      proxyType={selectedProxyType}
                      selectedProxy={selectedProxy}
                      onSelect={onSelectCategory}
                      rawInvestmentReturnsRanges={rawInvestmentReturnsRanges}
                      selectedProxyType={selectedProxyType}
                      numLags={numLags || suggestedNumLags}
                      extrapolate={shouldExtrapolate}
                    />
                  ) : (
                    <div>
                      <BulkProxySearch
                        selectedProxy={selectedProxy}
                        setSelectedProxy={setSelectedProxy}
                        rawInvestmentReturnsRanges={rawInvestmentReturnsRanges}
                        selectedProxyType={selectedProxyType}
                        investments={investments}
                        shouldExtrapolate={shouldExtrapolate}
                        autoProxyEnabled
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {!hasLayeredProxyFF && (
              <div className="mt-auto">
                <ProxySummaryTable
                  proxyType={selectedProxyType}
                  investments={investments}
                  validationState={validationState}
                  proxyDataByFund={returnsProxyDataByFund}
                />
              </div>
            )}

            {hasLayeredProxyFF && isLayered(stateResult) && (
              <ProxyConfigurationTabs
                configuration={stateResult.state.configuration}
                hasSingleInvestment={hasSingleInvestment}
                supportsCategoryPicker={supportsCategoryPicker}
                selectedProxyType={selectedProxyType}
                selectedProxy={selectedProxy}
                rawInvestmentReturnsRanges={rawInvestmentReturnsRanges}
                effectiveSecondLayerRanges={effectiveSecondLayerRanges}
                investments={investments}
                shouldExtrapolate={shouldExtrapolate}
                numLags={numLags}
                suggestedNumLags={suggestedNumLags}
                validationState={validationState}
                returnsProxyDataByFund={returnsProxyDataByFund}
                activeTab={stateResult.state.activeTab}
                performAction={performAction}
                showRequireUpdatingOtherProxy={showRequireUpdatingOtherProxy}
              />
            )}
          </div>
        </SidePanelOverlay.Body>
        <SidePanelOverlay.Footer
          cancelLabel="CANCEL"
          onCancel={isUpdating ? undefined : onClose}
          onPrimaryClick={() => performAction(ProxyPickerAction.Save())}
          primaryLabel={proceedMessage}
          primaryDisabled={validationState.state === 'loading' || !!validationState.error || isUpdating}
          {...removeProxyProps}
          fixed
        />
      </div>
    </>
  );
};

export const BulkProxyPicker = withSuspense(
  <>
    <ProxyPickerHeader title={HEADER_TITLE} subtitle={undefined} />
    <ShimmerBlock />
  </>,
  BulkProxyPickerInternal,
);

const SearchLabel = styled.div`
  font-weight: bold;
  font-size: 14px;
`;
